import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { SignUpHeader } from '../../components/SignUpHeader';
import { PrimaryButton } from '../../components/PrimaryButton';
import { SecondaryButton } from '../../components/SecondaryButton';
import { verifyEmail } from '../../../lib/professional-api';
import { setAuth, patchUser } from '../../../lib/auth';
import { apiErrorMessage } from '../../../lib/api';

type ScreenState =
  | { kind: 'loading' }
  | { kind: 'success'; message: string }
  | { kind: 'already-verified'; message: string }
  | { kind: 'error'; message: string };

/**
 * Lands here when the pro clicks the button in their verification email.
 * The URL looks like /professional/verify-email?token=xxx. We POST the
 * token to the backend, then route based on the response:
 *
 *   alreadyVerified: false → success view → /professional/verify-identity
 *   alreadyVerified: true  → "already verified" view + Sign in
 *   400                    → error view + offer to resend / restart
 */
export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [state, setState] = useState<ScreenState>({ kind: 'loading' });
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    if (!token) {
      setState({
        kind: 'error',
        message:
          'This link is missing its verification token. Open it from the email we sent.',
      });
      return;
    }

    (async () => {
      try {
        const res = await verifyEmail(token);
        // Re-clicks on a still-valid link now also return a session, so
        // the pro can resume onboarding instead of bouncing to sign-in.
        // Treat that path like a fresh verification.
        if (res.accessToken && res.user) {
          setAuth(res.accessToken, res.user);
          patchUser({ email_verified_at: new Date().toISOString() });
          setState({ kind: 'success', message: res.message });
        } else if (res.alreadyVerified) {
          setState({ kind: 'already-verified', message: res.message });
        }
      } catch (err) {
        setState({
          kind: 'error',
          message: apiErrorMessage(
            err,
            'This verification link is invalid or has expired.',
          ),
        });
      }
    })();
  }, [token]);

  return (
    <div className="size-full bg-white flex flex-col min-h-screen">
      <SignUpHeader mode="signin" isProfessional={true} />
      <main className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-[420px] mx-auto flex flex-col items-center text-center gap-6">
          {state.kind === 'loading' ? (
            <>
              <div className="w-12 h-12 rounded-full border-4 border-[#8e2a2f]/20 border-t-[#8e2a2f] animate-spin" />
              <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[28px] leading-[40px]">
                Verifying your email…
              </h1>
              <p className="text-[#908e8e] text-[14px]">
                Hang tight, this only takes a second.
              </p>
            </>
          ) : null}

          {state.kind === 'success' ? (
            <>
              <span className="material-icons text-[#8e2a2f] text-[56px]">
                {''}
              </span>
              <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[28px] md:text-[34px] leading-[40px] md:leading-[48px]">
                Email verified!
              </h1>
              <p className="text-[#908e8e] text-[14px] leading-[21px]">
                {state.message} You can carry on with identity verification
                now — that's the last step before customers can book you.
              </p>
              <PrimaryButton
                className="w-full py-4"
                onClick={() => navigate('/professional/verify-identity')}
              >
                Continue to identity check
              </PrimaryButton>
            </>
          ) : null}

          {state.kind === 'already-verified' ? (
            <>
              <span className="material-icons text-[#8e2a2f] text-[56px]">
                {''}
              </span>
              <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[28px] md:text-[34px] leading-[40px] md:leading-[48px]">
                Already verified
              </h1>
              <p className="text-[#908e8e] text-[14px] leading-[21px]">
                {state.message}
              </p>
              <PrimaryButton
                className="w-full py-4"
                onClick={() => navigate('/professional/signin')}
              >
                Sign in
              </PrimaryButton>
            </>
          ) : null}

          {state.kind === 'error' ? (
            <>
              <span className="material-icons text-[#8e2a2f] text-[56px]">
                {''}
              </span>
              <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[28px] md:text-[34px] leading-[40px] md:leading-[48px]">
                We couldn't verify that link
              </h1>
              <p className="text-[#908e8e] text-[14px] leading-[21px]">
                {state.message}
              </p>
              <Link to="/professional/signin" className="w-full">
                <PrimaryButton className="w-full py-4">Sign in</PrimaryButton>
              </Link>
              <Link to="/professional/signup" className="w-full">
                <SecondaryButton className="w-full py-4">
                  Sign up again
                </SecondaryButton>
              </Link>
            </>
          ) : null}
        </div>
      </main>
    </div>
  );
}
